// frontend/src/pages/ChurchManagementPage.tsx

import React, { useState, useEffect } from 'react';
import { useError } from '../contexts/ErrorContext';
import Navigation from '../components/Navigation/Navigation';
import {
  getAllChurches,
  ChurchWithStats,
  ServiceDefinition,
  getServiceDefinitionsByChurch,
  deleteChurch,
  deleteServiceDefinition,
  ChurchUsageStats,
  getChurchUsageStats,
  updateChurch
} from '../services/churchService';
import { ChevronDown, ChevronUp, Plus, Edit2, Trash2, Building2, Calendar } from 'lucide-react';
import { AddEditChurchModal } from '../components/churches/AddEditChurchModal';
import { AddEditServiceDefinitionModal } from '../components/churches/AddEditServiceDefinitionModal';
import { DeleteChurchModal } from '../components/churches/DeleteChurchModal';
import { useConfirmModal } from '../components/common/ConfirmModal';
import './ChurchManagementPage.css';

const ChurchManagementPage: React.FC = () => {
  const { confirm, ConfirmModal } = useConfirmModal();
  const [churches, setChurches] = useState<ChurchWithStats[]>([]);
  const [expandedChurchId, setExpandedChurchId] = useState<number | null>(null);
  const [serviceDefinitions, setServiceDefinitions] = useState<{ [churchId: number]: ServiceDefinition[] }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showAddChurchModal, setShowAddChurchModal] = useState(false);
  const [editingChurch, setEditingChurch] = useState<ChurchWithStats | null>(null);
  const [deletingChurch, setDeletingChurch] = useState<ChurchWithStats | null>(null);
  const [deleteStats, setDeleteStats] = useState<ChurchUsageStats | null>(null);
  const [showServiceDefModal, setShowServiceDefModal] = useState(false);
  const [currentChurchId, setCurrentChurchId] = useState<number | null>(null);
  const [editingServiceDef, setEditingServiceDef] = useState<ServiceDefinition | null>(null);
  const { setError } = useError();

  const loadChurches = async () => {
    try {
      setIsLoading(true);
      const data = await getAllChurches();
      setChurches(data);
    } catch (error) {
      setError(error instanceof Error ? error : new Error('Failed to load churches'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadServiceDefinitions = async (churchId: number) => {
    try {
      const defs = await getServiceDefinitionsByChurch(churchId);
      setServiceDefinitions(prev => ({ ...prev, [churchId]: defs }));
    } catch (error) {
      setError(error instanceof Error ? error : new Error('Failed to load service definitions'));
    }
  };

  useEffect(() => {
    loadChurches();
  }, []);

  const handleToggleExpand = async (churchId: number) => {
    if (expandedChurchId === churchId) {
      setExpandedChurchId(null);
    } else {
      setExpandedChurchId(churchId);
      if (!serviceDefinitions[churchId]) {
        await loadServiceDefinitions(churchId);
      }
    }
  };

  const handleAddChurch = () => {
    setEditingChurch(null);
    setShowAddChurchModal(true);
  };

  const handleEditChurch = (church: ChurchWithStats) => {
    setEditingChurch(church);
    setShowAddChurchModal(true);
  };

  const handleDeleteChurch = async (church: ChurchWithStats) => {
    setDeletingChurch(church);
    try {
      const stats = await getChurchUsageStats(church.id);
      setDeleteStats(stats);
    } catch (error) {
      setError(error instanceof Error ? error : new Error('Failed to load church usage stats'));
    }
  };

  const confirmDeleteChurch = async (force: boolean = false) => {
    if (!deletingChurch) return;

    try {
      await deleteChurch(deletingChurch.id, force);
      setDeletingChurch(null);
      setDeleteStats(null);
      await loadChurches();
    } catch (error) {
      setError(error instanceof Error ? error : new Error('Failed to delete church'));
    }
  };

  const handleAddServiceDefinition = (churchId: number) => {
    setCurrentChurchId(churchId);
    setEditingServiceDef(null);
    setShowServiceDefModal(true);
  };

  const handleEditServiceDefinition = (serviceDef: ServiceDefinition) => {
    setCurrentChurchId(serviceDef.church_id);
    setEditingServiceDef(serviceDef);
    setShowServiceDefModal(true);
  };

  const handleDeleteServiceDefinition = (serviceDef: ServiceDefinition) => {
    confirm({
      title: 'Delete Service Definition',
      message: `Delete service definition "${serviceDef.service_name}"?`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deleteServiceDefinition(serviceDef.service_def_id);
          await loadServiceDefinitions(serviceDef.church_id);
        } catch (error) {
          setError(error instanceof Error ? error : new Error('Failed to delete service definition'));
        }
      }
    });
  };

  const handleToggleChurchActive = async (church: ChurchWithStats) => {
    try {
      await updateChurch(church.id, { is_active: !church.is_active });
      await loadChurches();
    } catch (error) {
      setError(error instanceof Error ? error : new Error('Failed to toggle church status'));
    }
  };

  const getDayName = (dayOfWeek: number): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek] || '';
  };

  if (isLoading) {
    return (
      <div className="church-management-page">
        <Navigation />
        <div className="container mt-4">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="church-management-page">
      <Navigation />

      <div className="container-fluid mt-4">
        <div className="row mb-4">
          <div className="col">
            <div className="d-flex justify-content-between align-items-center">
              <h2>
                <Building2 className="me-2" size={32} />
                Church Management
              </h2>
              <button
                className="btn btn-primary"
                onClick={handleAddChurch}
              >
                <Plus size={18} className="me-1" />
                Add Church
              </button>
            </div>
          </div>
        </div>

        {churches.length === 0 ? (
          <div className="alert alert-info">
            No churches found. Click "Add Church" to create your first church.
          </div>
        ) : (
          <div className="row">
            {churches.map(church => (
              <div key={church.id} className="col-12 mb-3">
                <div className="card">
                  <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1">
                        <h5 className="mb-0">{church.name}</h5>
                        {church.address && (
                          <small className="text-muted">{church.address}</small>
                        )}
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <div className="form-check form-switch me-2" title={church.is_active ? 'Active - click to deactivate' : 'Inactive - click to activate'}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id={`church-active-${church.id}`}
                            checked={church.is_active}
                            onChange={() => handleToggleChurchActive(church)}
                          />
                          <label className="form-check-label small" htmlFor={`church-active-${church.id}`}>
                            {church.is_active ? 'Active' : 'Inactive'}
                          </label>
                        </div>
                        <span className="badge bg-secondary">
                          {church.service_count} services
                        </span>
                        <span className="badge bg-info">
                          {church.service_definition_count} definitions
                        </span>
                        <span className="badge bg-success">
                          {church.template_count} templates
                        </span>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleEditChurch(church)}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteChurch(church)}
                        >
                          <Trash2 size={16} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleToggleExpand(church.id)}
                        >
                          {expandedChurchId === church.id ? (
                            <ChevronUp size={16} />
                          ) : (
                            <ChevronDown size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {expandedChurchId === church.id && (
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6>
                          <Calendar size={18} className="me-2" />
                          Service Definitions
                        </h6>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleAddServiceDefinition(church.id)}
                        >
                          <Plus size={16} className="me-1" />
                          Add Service Definition
                        </button>
                      </div>

                      {serviceDefinitions[church.id]?.length === 0 ? (
                        <div className="alert alert-info mb-0">
                          No service definitions found for this church.
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-sm table-hover">
                            <thead>
                              <tr>
                                <th>Service Name</th>
                                <th>Day</th>
                                <th>Time</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {serviceDefinitions[church.id]?.map(def => (
                                <tr key={def.service_def_id}>
                                  <td>{def.service_name}</td>
                                  <td>{getDayName(def.day_of_week)}</td>
                                  <td>{def.time}</td>
                                  <td>
                                    <span className={`badge ${def.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                      {def.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                  <td>
                                    <button
                                      className="btn btn-sm btn-outline-primary me-2"
                                      onClick={() => handleEditServiceDefinition(def)}
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDeleteServiceDefinition(def)}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <AddEditChurchModal
        show={showAddChurchModal}
        onHide={() => {
          setShowAddChurchModal(false);
          setEditingChurch(null);
        }}
        onSuccess={loadChurches}
        church={editingChurch}
      />

      <DeleteChurchModal
        show={deletingChurch !== null}
        onHide={() => {
          setDeletingChurch(null);
          setDeleteStats(null);
        }}
        onConfirm={confirmDeleteChurch}
        church={deletingChurch}
        usageStats={deleteStats}
      />

      {currentChurchId && (
        <AddEditServiceDefinitionModal
          show={showServiceDefModal}
          onHide={() => {
            setShowServiceDefModal(false);
            setEditingServiceDef(null);
            setCurrentChurchId(null);
          }}
          onSuccess={async () => {
            if (currentChurchId) {
              await loadServiceDefinitions(currentChurchId);
            }
          }}
          churchId={currentChurchId}
          serviceDefinition={editingServiceDef}
        />
      )}

      <ConfirmModal />
    </div>
  );
};

export default ChurchManagementPage;
